import { useEffect, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  Legend, AreaChart, Area, CartesianGrid,
} from 'recharts';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { usePermisos } from '../lib/permisos';
import { api } from '../lib/api';
import { useLive } from '../lib/useLive';
import { PeriodPicker, periodoQS, ponerPeriodo } from '../components/PeriodPicker';
import { TopBar } from '../components/TopBar';
import { ChartCard } from '../components/ChartCard';
import { Dona } from '../components/Dona';
import { Barras } from '../components/Barras';
import { ListaDetalle, PedidoLista } from '../components/ListaDetalle';
import { RAMPA, RESULTADO, NEUTRO, EJE, entero, porcentaje, enPeriodo } from '../lib/graficas';
import { usePeriodoInicial } from '../lib/preferencias';

interface DashboardData {
  scope: 'all' | 'tenant';
  rol: string;
  asesora: string | null;
  kpis: { pacientes: number; leads: number; citasTotal: number; citasMes: number; tasaAsistencia: number | null; quejas: number };
  embudo: { etapa: string; n: number }[];
  citasEstado: { status: string; n: number }[];
  porAsesora: { asesora: string; total: number; leads: number; pacientes: number }[];
  canales: { canal: string; n: number }[];
  tendencia: { mes: string; citas: number }[];
  periodo?: { label: string; all: boolean; citasSinFecha: number };
}

interface HoyData {
  fecha: string;
  altas: { hoy: number; semana: number; ultima: string | null; porOrigen: { origen: string; n: number }[] };
  citas: { creadas_hoy: number; para_hoy: number; creadas_semana: number; ultima: string | null; porVia: { via: string; n: number }[] };
  halory: {
    webhookConfigurado: boolean; apiKeyConfigurada: boolean;
    respuestasHoy: number; respuestasSemana: number; sinEmpatar: number; sinEmpatarAbiertos: number;
    ultimo: string | null; leadsVinculados: number; leadsHoy?: number; leadsSemana?: number; nota: string;
    sync?: { ultimo: string | null; motivo: string | null; altas: number | null; reasignados: number | null;
             pauta: { ultimo: string | null; filas: number | null; motivo: string | null } | null };
  };
  dinero: { tablas: boolean; ventas?: number; ventaHasta?: string | null; gastoFilas?: number; gastoHasta?: string | null };
  sinDueno: { sinAsesora: number; sinEtapa: number };
  /** Citas de Halory que no se pudieron ligar a un expediente (0.49.0); null = la tabla todavía no existe. */
  citasSinPaciente?: { pendientes: number; canceladas: number } | null;
  pendientes: { que: string; porque: string; donde: string }[];
}

const GOLD = '#AC996C';
const GOLD_DARK = '#8b7355';
const GOLD_LIGHT = '#cbb88e';
/** El estado de una cita, con el color de su resultado (el mismo de Analítica): llegó, canceló, no llegó; lo que sigue abierto, en dorado. */
const ESTADO_COLOR: Record<string, string> = {
  programada: '#b8862b', confirmada: '#8b7355', completada: RESULTADO.llego, cancelada: RESULTADO.cancelo, no_show: '#a39a8c', reagendada: '#6a4fb3', en_progreso: '#2a6fb5',
};
const ETAPA_CLAVE: Record<string, string> = { Leads: 'LEAD', 'Primera visita': 'PV', Recompra: 'SUB', UGC: 'UGC' };

export function Dashboard() {
  const { user } = useAuth();
  const { puede } = usePermisos();
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [periodo, setPeriodo] = useState(usePeriodoInicial());
  const [hoy, setHoy] = useState<HoyData | null>(null);
  // Lo que hay detrás de una barra (las personas o las citas), con el periodo de la pantalla.
  const [lista, setLista] = useState<PedidoLista | null>(null);
  const abrir = (p: PedidoLista) => setLista({ ...p, qs: Object.fromEntries(ponerPeriodo(new URLSearchParams(p.qs), periodo).entries()) });
  const load = (per = periodo) => { const qs = periodoQS(per); return api<DashboardData>('overview' + (qs ? `?${qs}` : '')).then(setData).catch((e) => setError(e.message)); };
  const loadHoy = () => api<HoyData>('hoy').then(setHoy).catch(() => {});
  useEffect(() => { load(); loadHoy(); }, []);
  // Se repinta solo cuando alguien registra un seguimiento o cambia una ficha.
  // La recarga es silenciosa (no se vacía `data`): el tablero no debe parpadear
  // por algo que hizo otra persona.
  useLive(['followup', 'patient', 'inbound'], () => { load(periodo); loadHoy(); });

  if (!user) return null;

  const scopeLabel = data
    ? data.rol === 'asesora'
      ? `Cartera de ${data.asesora}`
      : data.scope === 'all'
        ? 'Todas las clínicas'
        : user.tenantName
    : '';

  return (
    <div>
      <TopBar />
      <div style={{ maxWidth: 1080, margin: '0 auto', padding: '20px 16px 48px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, margin: '4px 2px 16px', flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: 300, color: GOLD_DARK, letterSpacing: -0.5 }}>Resumen</h1>
        {scopeLabel && <span className="badge">{scopeLabel}</span>}
        {data?.periodo && !data.periodo.all && <span className="badge" style={{ background: 'var(--gold-light)', color: 'var(--gold-dark)' }}>{data.periodo.label}</span>}
      </div>

      <div style={{ margin: '0 2px 16px' }}>
        <PeriodPicker value={periodo} onChange={(v) => { setPeriodo(v); load(v); }} />
      </div>

      {error && (
        <div className="card" style={{ padding: 18, color: 'var(--warning)', background: 'var(--warning-bg)' }}>
          No se pudo cargar el dashboard: {error}
        </div>
      )}

      {!data && !error && <div className="muted" style={{ padding: 40, textAlign: 'center' }}>Cargando datos…</div>}

      {hoy && <QueEntroHoy d={hoy} admin={user.role === 'admin'} conAlertas={puede('alertas')} />}
      {lista && <ListaDetalle pedido={lista} onClose={() => setLista(null)} />}

      {data && (
        <div className="fade-in" style={{ display: 'grid', gap: 16 }}>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
            <Kpi label="Pacientes" value={data.kpis.pacientes} />
            <Kpi label="Leads" value={data.kpis.leads} accent />
            <Kpi label="Citas" value={data.kpis.citasTotal} sub={`${data.kpis.citasMes} este mes`} />
            <Kpi label="Asistencia" value={data.kpis.tasaAsistencia != null ? `${data.kpis.tasaAsistencia}%` : '—'} />
            <Kpi label="Quejas activas" value={data.kpis.quejas} warn={data.kpis.quejas > 0} />
          </div>

          {/* Etapa + cómo terminan las citas */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
            <ChartCard title="¿En qué etapa está cada quien?" datos="resumen-etapa" data={data.embudo}
              como={`De quienes llegaron ${enPeriodo(periodo)}: si nunca han venido (lead), vinieron una vez o ya volvieron. Toca una etapa para ver quiénes son.`}
              vacio="Nadie llegó en este periodo."
              render={() => (
                <Barras filas={data.embudo.map((e, i) => ({ clave: e.etapa, nombre: e.etapa, n: e.n, color: RAMPA[Math.min(5, i + 1)] }))} datos="resumen-etapa"
                  onAbrir={(k) => abrir({ titulo: k, subtitulo: 'Etapa de hoy: con las visitas que de verdad pasaron.', qs: { tipo: 'embudo', paso: 'contactos', etapa: ETAPA_CLAVE[k] || k } })} />
              )} />
            <ChartCard title="¿Cómo terminan las citas?" datos="resumen-citas" data={data.citasEstado}
              como="Las citas que se generaron en el periodo, por cómo están hoy. Toca un estado para ver las citas."
              vacio="No se generaron citas en este periodo."
              render={(h) => (
                <Dona datos={[...data.citasEstado].sort((a, b) => b.n - a.n).map((e) => ({ clave: e.status, nombre: ESTADO_TXT[e.status] || e.status, n: e.n, color: ESTADO_COLOR[e.status] || NEUTRO }))}
                  alto={Math.min(h, 230)} unidad="citas" datosTest="citas-estado"
                  onAbrir={(k) => abrir({ titulo: `Citas: ${(ESTADO_TXT[k] || k).toLowerCase()}`, qs: { tipo: 'agenda', estado: k } })} />
              )} />
          </div>

          {/* Por asesora (oculto si es asesora: solo se ve a sí misma) */}
          {data.porAsesora.length > 1 && (
            <ChartCard title="¿Cuánta gente tiene cada asesoría?" datos="resumen-asesoria" data={data.porAsesora}
              como="Expedientes asignados a cada quien (de los que llegaron en el periodo): cuántos ya vinieron, cuántos son leads y cuántos no tienen etapa."
              render={(h) => (
                <ResponsiveContainer width="100%" height={h + 20}>
                  <BarChart data={data.porAsesora.map((a) => ({ ...a, otros: Math.max(0, a.total - a.leads - a.pacientes) }))} margin={{ left: -10, right: 10, top: 6 }}>
                    <CartesianGrid stroke="#efece6" vertical={false} />
                    <XAxis dataKey="asesora" tick={{ fontSize: 11, fill: '#6b6b6b' }} axisLine={false} tickLine={false} interval={0} />
                    <YAxis tick={EJE} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: 'rgba(172,153,108,0.08)' }} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span style={{ color: 'var(--ink)' }}>{v}</span>} />
                    <Bar dataKey="pacientes" name="Ya vinieron" stackId="a" fill={GOLD_DARK} maxBarSize={44} isAnimationActive={false} />
                    <Bar dataKey="leads" name="Leads" stackId="a" fill={GOLD_LIGHT} maxBarSize={44} isAnimationActive={false} />
                    <Bar dataKey="otros" name="Sin etapa" stackId="a" fill={NEUTRO} radius={[4, 4, 0, 0]} maxBarSize={44} isAnimationActive={false} />
                  </BarChart>
                </ResponsiveContainer>
              )} />
          )}

          {/* Canales + Tendencia */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', gap: 16 }}>
            <ChartCard title="¿Por dónde llegan?" datos="resumen-canales" data={data.canales.filter((c) => c.canal !== 'Sin dato')}
              como={`El canal por el que llegó cada expediente nuevo. Toca uno para ver quiénes son.${(() => { const sd = data.canales.find((c) => c.canal === 'Sin dato'); return sd ? ` ${entero(sd.n)} no tienen el canal capturado (${porcentaje(sd.n, data.canales.reduce((a, c) => a + c.n, 0))}).` : ''; })()}`}
              vacio="Nadie llegó en este periodo, o nadie tiene el canal capturado."
              render={() => (
                <Barras filas={data.canales.filter((c) => c.canal !== 'Sin dato').map((c) => ({ clave: c.canal, nombre: c.canal, n: c.n }))} datos="resumen-canales"
                  onAbrir={(k) => abrir({ titulo: `Llegaron por ${k}`, qs: { tipo: 'embudo', paso: 'contactos', por: 'canal', valor: k } })} />
              )} />

            <ChartCard title="¿Cuántas citas se generan al mes?" datos="resumen-tendencia" data={data.tendencia} hint="todo el historial"
              como="Por el mes en que se generó cada cita. No se recorta por el periodo: es el contexto."
              render={(h) => (
                <ResponsiveContainer width="100%" height={h}>
                  <AreaChart data={data.tendencia} margin={{ left: -16, right: 12, top: 6 }}>
                    <defs>
                      <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={GOLD} stopOpacity={0.35} />
                        <stop offset="100%" stopColor={GOLD} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#efece6" vertical={false} />
                    <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b6b6b' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={EJE} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ stroke: GOLD, strokeWidth: 1 }} formatter={(v: any) => [entero(Number(v)), 'Citas']} />
                    <Area type="monotone" dataKey="citas" stroke={GOLD_DARK} strokeWidth={2} fill="url(#gGold)" isAnimationActive={false} />
                  </AreaChart>
                </ResponsiveContainer>
              )} />
          </div>

          <p className="muted" style={{ fontSize: 12, textAlign: 'center', marginTop: 4 }}>
            Datos en vivo del mismo PostgreSQL de DraIA · fuente única de verdad.
          </p>
        </div>
      )}
      </div>
    </div>
  );
}

/**
 * "Qué entró hoy" — el cero con su razón.
 *
 * POR QUÉ EXISTE: la pregunta fue literal — *"dice que ahorita no hay nuevos
 * registros de hoy, ¿es correcto?"* — y desde la app no había manera de
 * saberlo. Un tablero en cero se ve idéntico en tres casos que no se parecen
 * en nada: que de verdad no pasó nada, que sí pasó pero cayó en una forma que
 * este tablero no cuenta, o que la fuente ni siquiera está conectada. La
 * diferencia entre ellos es si alguien debería estar marcando un teléfono
 * ahorita mismo.
 *
 * Por eso cada fuente dice su estado, y lo que falta se enuncia como acción
 * concreta con el lugar donde se hace — no como un aviso que hay que
 * interpretar.
 */
function QueEntroHoy({ d, admin, conAlertas }: { d: HoyData; admin: boolean; conAlertas: boolean }) {
  const fecha = (s: string | null) => (s ? new Date(s).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
  const hayAlgo = d.altas.hoy + d.citas.creadas_hoy + d.citas.para_hoy + d.halory.respuestasHoy > 0;

  return (
    <div className="card" style={{ padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>Qué entró hoy</div>
        <span className="muted" style={{ fontSize: 12 }}>{/^\d{4}-\d{2}-\d{2}$/.test(d.fecha || '') ? new Date(d.fecha + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' }) : d.fecha}</span>
        {!hayAlgo && <span className="badge" style={{ background: 'var(--bg-alt)', color: 'var(--ink-soft)' }}>sin movimientos</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <Fuente n={d.altas.hoy} label="Expedientes nuevos"
                pie={`${d.altas.semana} en 7 días · último ${fecha(d.altas.ultima)}`} />
        <Fuente n={d.citas.creadas_hoy} label="Citas agendadas hoy"
                pie={`${d.citas.creadas_semana} en 7 días · último ${fecha(d.citas.ultima)}`} />
        <Fuente n={d.citas.para_hoy} label="Citas para hoy"
                pie="Quien viene hoy, sin importar cuándo se agendó" />
        <Fuente n={d.halory.respuestasHoy} label="Respondieron por Halory"
                pie={d.halory.webhookConfigurado
                  ? `${d.halory.respuestasSemana} en 7 días · último ${fecha(d.halory.ultimo)}`
                  : 'Receptor apagado: falta HALORY_WEBHOOK_SECRET'}
                apagada={!d.halory.webhookConfigurado} />
      </div>

      {/* Lo que llega por Halory y NO empata con nadie. Es el caso que el
          tablero no puede contar como paciente y que aun así hay que atender. */}
      {d.halory.sinEmpatarAbiertos > 0 && (
        <div style={{ marginTop: 10, fontSize: 13 }}>
          <strong>{d.halory.sinEmpatarAbiertos}</strong> escribieron y no están en la base{conAlertas ? <> — se ven en <Link to="/alertas" style={{ color: 'var(--gold-dark)', fontWeight: 600 }}>Alertas</Link></> : ''}.
        </div>
      )}

      {/* Citas de Halory que llegaron sin contacto y no empataron con un expediente (0.49.0): se asignan en Analítica → Citas. */}
      {(d.citasSinPaciente?.pendientes ?? 0) > 0 && (
        <div data-hoy-citas-sin-paciente style={{ marginTop: 10, fontSize: 13 }}>
          <strong>{d.citasSinPaciente!.pendientes}</strong> {d.citasSinPaciente!.pendientes === 1 ? 'cita de Halory sin paciente' : 'citas de Halory sin paciente'}
          {d.citasSinPaciente!.canceladas > 0 ? ` (${d.citasSinPaciente!.canceladas} canceladas en Halory)` : ''}
          {admin ? <> → <Link to="/analitica?tab=citas" style={{ color: 'var(--gold-dark)', fontWeight: 600 }}>Analítica → Citas</Link></> : ' → se asignan en Analítica → Citas'}.
        </div>
      )}

      {/* Lo que el reloj le preguntó a Halory: quién atiende, leads que faltaban y el gasto de pauta. */}
      {d.halory.apiKeyConfigurada && d.halory.sync?.ultimo && (
        <div data-hoy-sync style={{ marginTop: 10, fontSize: 13 }}>
          {d.halory.sync.motivo
            ? <span style={{ color: 'var(--warning)' }}>Halory no contestó en la última revisión ({fecha(d.halory.sync.ultimo)}): {d.halory.sync.motivo}</span>
            : <span className="muted">Halory revisado {fecha(d.halory.sync.ultimo)}
                {(d.halory.leadsHoy ?? 0) > 0 ? ` · ${d.halory.leadsHoy} lead${d.halory.leadsHoy === 1 ? '' : 's'} vinculado${d.halory.leadsHoy === 1 ? '' : 's'} hoy` : ''}
                {d.halory.sync.pauta?.ultimo && !d.halory.sync.pauta.motivo ? ` · gasto de pauta al ${fecha(d.halory.sync.pauta.ultimo)}` : ''}</span>}
        </div>
      )}

      <div className="muted" style={{ fontSize: 12, marginTop: 10, lineHeight: 1.5 }}>
        {d.halory.nota}
        {d.altas.porOrigen.length > 0 && (
          <> Altas de los últimos 7 días por origen: {d.altas.porOrigen.map((o) => `${o.origen} (${o.n})`).join(', ')}.</>
        )}
      </div>

      {d.pendientes.length > 0 && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>Lo que falta para que estos números estén completos</div>
          {d.pendientes.map((p, i) => (
            <div key={i} style={{ fontSize: 13, marginBottom: 6 }}>
              · <strong>{p.que}</strong>
              <div className="muted" style={{ fontSize: 12, marginLeft: 10 }}>{p.porque} — {p.donde}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** El estado guardado de una cita, en palabras (la gráfica enseñaba "no_show" tal cual). */
const ESTADO_TXT: Record<string, string> = {
  programada: 'Programada', confirmada: 'Confirmada', completada: 'Completada', cancelada: 'Cancelada',
  no_show: 'No asistió', reagendada: 'Reagendada', pendiente: 'Pendiente',
};

function Fuente({ n, label, pie, apagada }: { n: number; label: string; pie: string; apagada?: boolean }) {
  return (
    <div style={{ background: 'var(--bg-alt)', borderRadius: 12, padding: '10px 12px', opacity: apagada ? 0.65 : 1 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: apagada ? 'var(--ink-soft)' : n > 0 ? 'var(--gold-dark)' : 'var(--ink)' }}>
        {apagada ? '—' : n}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
      <div className="muted" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.4, overflowWrap: 'anywhere' }}>{pie}</div>
    </div>
  );
}

function Kpi({ label, value, sub, accent, warn }: { label: string; value: number | string; sub?: string; accent?: boolean; warn?: boolean }) {
  const color = warn ? 'var(--warning)' : accent ? GOLD_DARK : 'var(--ink)';
  return (
    <div className="card" style={{ padding: '16px 18px', minWidth: 0 }}>
      <div className="muted" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 'clamp(22px, 7vw, 30px)', fontWeight: 700, color, marginTop: 4, lineHeight: 1.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}
